import type { CashMovementDto } from './cash-movement.dto.js';
import type { CashDenominationDto } from './cash-sessions.dto.js';
import type { CashOperationRequestIdentity } from './cash-operation-identity.dto.js';
import type { CashModeScopeType } from './cash-operating-mode.dto.js';

export const cashVarianceStageValues = ['open', 'close', 'audit'] as const;
export type CashVarianceStage = (typeof cashVarianceStageValues)[number];

export const cashVarianceCaseStatusValues = [
  'OPEN',
  'UNDER_REVIEW',
  'PARTIALLY_RESOLVED',
  'RESOLVED',
] as const;
export type CashVarianceCaseStatus = (typeof cashVarianceCaseStatusValues)[number];

export const cashVarianceActionTypeValues = [
  'recovery_submitted',
  'recovery_approved',
  'recovery_rejected',
  'writeoff_applied',
  'offset_applied',
  'overage_classified',
  'overage_removed',
  'note',
] as const;
export type CashVarianceActionType = (typeof cashVarianceActionTypeValues)[number];

export const cashVarianceActionStatusValues = [
  'pending',
  'approved',
  'rejected',
  'applied',
] as const;
export type CashVarianceActionStatus = (typeof cashVarianceActionStatusValues)[number];

export const cashVarianceRecoveryDirectionValues = ['cash_in', 'cash_out'] as const;
export type CashVarianceRecoveryDirection = (typeof cashVarianceRecoveryDirectionValues)[number];

export const cashVarianceOverageClassificationValues = [
  'retained_overage',
  'found_cash',
  'customer_correction',
  'accounting_pending',
  'other',
] as const;
export type CashVarianceOverageClassification =
  (typeof cashVarianceOverageClassificationValues)[number];

export interface CashVarianceDenominationCaptureDto {
  version: 1;
  effectiveMode: 'denomination_inventory';
  scopeType: CashModeScopeType;
  scopeId: string;
  scopeModeStateId: string;
  configurationRevision: number;
  entitlementRevision: number;
}

// Monetary amounts are in minor units (e.g., cents). Signed amounts are direction-aware.
export interface CashVarianceCaseDto {
  id: string;
  companyId: string;
  cashLocationId: string;
  cashSessionId: string;
  stage: CashVarianceStage;
  initialAmountMinor: number;
  outstandingAmountMinor: number;
  recoveredAmountMinor: number;
  writtenOffAmountMinor: number;
  status: CashVarianceCaseStatus;
  openedByUserId: string;
  openedAt: string;
  resolvedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

// Monetary amounts are in minor units (e.g., cents). Signed amounts are direction-aware.
export interface CashVarianceActionDto {
  id: string;
  companyId: string;
  caseId: string;
  actionType: CashVarianceActionType;
  status: CashVarianceActionStatus;
  amountMinor: number;
  recoveryDirection: CashVarianceRecoveryDirection | null;
  denominations: CashDenominationDto[] | null;
  denominationCapture?: CashVarianceDenominationCaptureDto | null;
  note: string | null;
  submissionActionId: string | null;
  targetCashLocationId: string | null;
  targetCashSessionId: string | null;
  linkedMovementId: string | null;
  relatedCaseId?: string | null;
  relatedActionId?: string | null;
  classification?: CashVarianceOverageClassification | null;
  submittedByUserId: string;
  reviewedByUserId: string | null;
  submittedAt: string;
  reviewedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ListCashVarianceCasesRequest {
  companyId: string;
  status?: CashVarianceCaseStatus;
  cashLocationId?: string;
  stationId?: string;
  storeId?: string;
  locationId?: string;
  from?: string;
  to?: string;
}

export interface ListCashVarianceCasesResponse {
  items: CashVarianceCaseDto[];
}

export interface GetCashVarianceCaseRequest {
  companyId: string;
  caseId: string;
}

export interface GetCashVarianceCaseResponse {
  cashVarianceCase: CashVarianceCaseDto;
  actions: CashVarianceActionDto[];
  linkedMovementIds: string[];
}

export interface SubmitCashVarianceRecoveryRequest extends CashOperationRequestIdentity {
  occurredAt: string;
  companyId: string;
  caseId: string;
  direction: CashVarianceRecoveryDirection;
  amountMinor: number;
  denominations?: CashDenominationDto[] | null;
  note?: string | null;
}

export interface SubmitCashVarianceRecoveryResponse {
  cashVarianceCase: CashVarianceCaseDto;
  action: CashVarianceActionDto;
}

export interface ApproveCashVarianceRecoveryRequest extends CashOperationRequestIdentity {
  occurredAt: string;
  companyId: string;
  actionId: string;
  targetCashLocationId: string;
  storeId?: string;
  locationId?: string;
  stationId?: string;
}

export interface ApproveCashVarianceRecoveryResponse {
  cashVarianceCase: CashVarianceCaseDto;
  action: CashVarianceActionDto;
  movement: CashMovementDto;
}

export interface RejectCashVarianceRecoveryRequest extends CashOperationRequestIdentity {
  occurredAt: string;
  companyId: string;
  actionId: string;
  note?: string | null;
}

export interface RejectCashVarianceRecoveryResponse {
  cashVarianceCase: CashVarianceCaseDto;
  action: CashVarianceActionDto;
}

export interface ApplyCashVarianceWriteoffRequest extends CashOperationRequestIdentity {
  occurredAt: string;
  companyId: string;
  caseId: string;
  amountMinor: number;
  note: string;
}

export interface ApplyCashVarianceWriteoffResponse {
  cashVarianceCase: CashVarianceCaseDto;
  action: CashVarianceActionDto;
}

export interface ApplyCashVarianceOffsetRequest extends CashOperationRequestIdentity {
  occurredAt: string;
  companyId: string;
  shortageCaseId: string;
  overageCaseId: string;
  amountMinor: number;
  note: string;
}

export interface ApplyCashVarianceOffsetResponse {
  shortageCase: CashVarianceCaseDto;
  overageCase: CashVarianceCaseDto;
  shortageAction: CashVarianceActionDto;
  overageAction: CashVarianceActionDto;
}

export interface ClassifyCashVarianceOverageRequest extends CashOperationRequestIdentity {
  occurredAt: string;
  companyId: string;
  caseId: string;
  amountMinor: number;
  classification: CashVarianceOverageClassification;
  note: string;
}

export interface ClassifyCashVarianceOverageResponse {
  cashVarianceCase: CashVarianceCaseDto;
  action: CashVarianceActionDto;
}

export interface RemoveCashVarianceOverageRequest extends CashOperationRequestIdentity {
  occurredAt: string;
  companyId: string;
  caseId: string;
  amountMinor: number;
  targetCashLocationId: string;
  storeId?: string;
  locationId?: string;
  stationId?: string;
  denominations?: CashDenominationDto[] | null;
  note: string;
}

export interface RemoveCashVarianceOverageResponse {
  cashVarianceCase: CashVarianceCaseDto;
  action: CashVarianceActionDto;
  movement: CashMovementDto;
}
